import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Variant } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { session } from '@/shared/session/session';

import { storePricingQueryOptions } from '../hooks/use-store-pricing';
import { storeMembershipQueryOptions } from '../hooks/use-store-products';
import { priceSource } from '../schemas/pricing-rules';

import { StorePricesModal } from './store-prices-modal';

export interface VariantStorePricesProps {
  productId: string;
  variant: Variant;
}

/** A master variant's View more: what it sells at in each store and where that price comes from. */
export function VariantStorePrices({ productId, variant }: VariantStorePricesProps) {
  const { t } = useTranslation(['storeCatalog', 'products']);
  const format = useFormat();
  const { me } = session.useSession();
  const [open, setOpen] = useState(false);
  const stores = me?.stores ?? [];
  const memberships = useQueries({
    queries: stores.map((store) => storeMembershipQueryOptions(store.id, productId)),
  });
  const selling = stores.flatMap((store, at) => {
    const row = memberships[at]?.data;
    return row ? [{ store, storeProductId: row.id }] : [];
  });
  const pricing = useQueries({
    queries: selling.map(({ store, storeProductId }) =>
      storePricingQueryOptions(store.id, storeProductId),
    ),
  });
  const pending =
    memberships.some((query) => query.isPending) || pricing.some((query) => query.isPending);

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-[13px] font-semibold">
          {t('products:variantsCard.groups.storePrices')}
        </h4>
        <Can permission="products.write">
          <Button
            type="button"
            variant="link"
            size="xs"
            className="text-primary h-auto p-0 text-[13px]"
            aria-label={t('storeCatalog:storeVariants.editStorePricesFor', {
              variant: variant.title,
            })}
            onClick={() => setOpen(true)}
          >
            {t('storeCatalog:storeVariants.editStorePrices')}
          </Button>
        </Can>
      </div>
      {pending ? (
        <Skeleton className="h-10" />
      ) : selling.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">
          {t('storeCatalog:storeVariants.inNoStore')}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5 text-[13px]">
          {selling.map(({ store }, at) => {
            const row = pricing[at]?.data?.find((entry) => entry.variantId === variant.id);
            return (
              <li key={store.id} className="flex justify-between gap-2">
                <span className="truncate">{store.name}</span>
                <span className="whitespace-nowrap tabular-nums">
                  {row?.effective ? format.money(row.effective) : '—'}
                  {row ? (
                    <span className="text-muted-foreground">
                      {' · '}
                      {t(`storeCatalog:pricing.source.${priceSource(row)}`)}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {open ? (
        <StorePricesModal productId={productId} variant={variant} open onOpenChange={setOpen} />
      ) : null}
    </div>
  );
}
